import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { emitHealthDataUpdated } from "../lib/healthSync";

const METRIC_CONFIG = {
  "heart-rate": {
    label: "Heart Rate",
    category: "Vitals",
    kind: "vitals",
    field: "heartRate",
    inputType: "number",
    min: 30,
    max: 220,
    unit: "bpm",
    note: "Heart rate is the number of heart beats per minute. Resting heart rate is usually 60-100 bpm for most adults.",
  },
  "blood-pressure": {
    label: "Blood Pressure",
    category: "Vitals",
    kind: "vitals",
    field: "bloodPressure",
    inputType: "text",
    placeholder: "e.g. 120/80",
    note: "Blood pressure has two numbers: systolic/diastolic. A common healthy range is around 120/80 mmHg.",
  },
  "oxygen-level": {
    label: "Oxygen Level",
    category: "Vitals",
    kind: "vitals",
    field: "oxygenLevel",
    inputType: "number",
    min: 50,
    max: 100,
    unit: "%",
    note: "Oxygen saturation (SpO2) shows how much oxygen your blood carries. Typical healthy values are 95% or above.",
  },
  "blood-sugar": {
    label: "Blood Sugar",
    category: "Vitals",
    kind: "vitals",
    field: "bloodSugar",
    inputType: "number",
    min: 20,
    max: 600,
    unit: "mg/dL",
    note: "Blood sugar helps track glucose levels. Typical fasting targets are often around 70 to 99 mg/dL unless your clinician advises otherwise.",
  },
  temperature: {
    label: "Temperature",
    category: "Vitals",
    kind: "vitals",
    field: "temperature",
    inputType: "number",
    min: 90,
    max: 110,
    step: 0.1,
    unit: "F",
    note: "Body temperature helps detect fever and infection. Normal is often around 97F to 99.5F.",
  },
  weight: {
    label: "Weight",
    category: "Body",
    kind: "vitals",
    field: "weight",
    inputType: "number",
    min: 1,
    max: 500,
    step: 0.1,
    unit: "kg",
    note: "Weight trends are useful over time. Track regularly to monitor progress and overall health goals.",
  },
  activity: {
    label: "Activity",
    category: "Body",
    kind: "assessment",
    field: "fitnessLevel",
    inputType: "number",
    min: 0,
    max: 10,
    unit: "/10",
    note: "Activity level reflects daily movement and exercise intensity. Higher values generally indicate better physical activity.",
  },
  sleep: {
    label: "Sleep",
    category: "Lifestyle",
    kind: "assessment",
    field: "sleepLevel",
    inputType: "select",
    options: ["Excellent", "Good", "Fair", "Poor", "Insomniac"],
    note: "Sleep quality impacts energy, mood, immunity, and recovery. Consistent good sleep improves long-term health outcomes.",
  },
  hydration: {
    label: "Hydration",
    category: "Lifestyle",
    kind: "vitals",
    field: "hydration",
    inputType: "number",
    min: 0,
    max: 10000,
    unit: "ml/day",
    note: "Hydration tracks fluid intake. A common daily target is around 2000 ml, depending on weather and activity.",
  },
  mood: {
    label: "Mood",
    category: "Lifestyle",
    kind: "assessment",
    field: "mood",
    inputType: "select",
    options: ["Excellent", "Great", "Good", "Fair", "Bad", "Anxious", "Sad"],
    note: "Mood is an important mental health indicator. Logging mood helps identify stress patterns and emotional trends.",
  },
  appointments: {
    label: "Appointments",
    category: "Lifestyle",
    kind: "readonly",
    field: "appointments",
    note: "Upcoming appointments show planned consultations. Manage schedule, reschedule, or cancel from the appointment timeline.",
  },
  symptoms: {
    label: "Symptoms",
    category: "Lifestyle",
    kind: "readonly",
    field: "symptoms",
    note: "Symptoms are logged from your vitals/health records entries. Add or update symptom logs from your records page.",
  },
  medication: {
    label: "Medication",
    category: "Lifestyle",
    kind: "readonly",
    field: "medication",
    note: "Medication adherence is based on today's schedule and reminder actions (taken, skipped, snoozed). Manage doses from your care plan.",
  },
};

function parseBloodPressure(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
  if (!match) return null;
  const systolic = Number(match[1]);
  const diastolic = Number(match[2]);
  return {
    raw: `${systolic}/${diastolic}`,
    systolic,
    diastolic,
  };
}

function getMetricValidation(metricConfig, rawValue) {
  if (!metricConfig || metricConfig.kind === "readonly") {
    return { level: "neutral", message: "" };
  }

  const value = String(rawValue || "").trim();
  if (!value) {
    return { level: "neutral", message: "Enter a value to update this metric." };
  }

  if (metricConfig.field === "bloodPressure") {
    const bp = parseBloodPressure(value);
    if (!bp) {
      return {
        level: "warning",
        message: "Use format systolic/diastolic (example: 120/80).",
      };
    }

    const { systolic, diastolic } = bp;
    if (systolic >= 180 || diastolic >= 120) {
      return { level: "danger", message: "This reading is very high. Seek urgent medical help if symptoms are present." };
    }
    if (systolic >= 140 || diastolic >= 90) {
      return { level: "warning", message: "High blood pressure range. Consider contacting your clinician." };
    }
    if (systolic < 90 || diastolic < 60) {
      return { level: "warning", message: "Low blood pressure range. Recheck and monitor symptoms." };
    }
    return { level: "success", message: "Reading appears within a typical range." };
  }

  if (metricConfig.inputType === "number") {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return { level: "warning", message: "Enter a valid number." };
    }

    if (typeof metricConfig.min === "number" && n < metricConfig.min) {
      return { level: "warning", message: `Value is below suggested minimum (${metricConfig.min}).` };
    }
    if (typeof metricConfig.max === "number" && n > metricConfig.max) {
      return { level: "warning", message: `Value is above suggested maximum (${metricConfig.max}).` };
    }

    if (metricConfig.field === "oxygenLevel") {
      if (n < 90) return { level: "danger", message: "Very low oxygen saturation. Seek urgent care if persistent." };
      if (n < 95) return { level: "warning", message: "Slightly low oxygen saturation. Monitor closely." };
      return { level: "success", message: "Oxygen saturation looks healthy." };
    }

    if (metricConfig.field === "bloodSugar") {
      if (n < 70) return { level: "warning", message: "Low blood sugar range. Recheck and monitor symptoms." };
      if (n > 180) return { level: "warning", message: "High blood sugar range. Consider follow-up if persistent." };
      return { level: "success", message: "Blood sugar appears within a common target range." };
    }

    if (metricConfig.field === "temperature") {
      if (n >= 100.4) return { level: "warning", message: "Fever range. Hydrate and monitor symptoms." };
      if (n < 95) return { level: "warning", message: "Temperature is low. Recheck with a reliable thermometer." };
      return { level: "success", message: "Temperature appears in a common normal range." };
    }

    if (metricConfig.field === "heartRate") {
      if (n < 50 || n > 120) return { level: "warning", message: "Outside common resting range for many adults." };
      return { level: "success", message: "Heart rate appears in a common resting range." };
    }

    if (metricConfig.field === "hydration") {
      if (n < 1200) return { level: "warning", message: "Hydration is low for most adults. Consider increasing fluid intake." };
      if (n >= 2000) return { level: "success", message: "Hydration intake meets common daily goals." };
    }

    return { level: "success", message: "Value format looks good." };
  }

  return { level: "success", message: "Selection looks good." };
}

export default function MetricDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const metricConfig = METRIC_CONFIG[slug] || null;

  const [dashboardData, setDashboardData] = useState(null);
  const [assessmentRecord, setAssessmentRecord] = useState(null);
  const [latestVitalsRecord, setLatestVitalsRecord] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchAllData = useCallback(async () => {
    if (!metricConfig) return;

    setLoading(true);
    setError("");

    try {
      const [dashboardRes, assessmentRes, latestVitalsRes, summaryRes] = await Promise.all([
        api.get("/api/metrics/dashboard"),
        api.get("/api/assessment").catch(() => null),
        api.get("/api/vitals/latest").catch(() => null),
        api.get("/api/metrics/summary").catch(() => null),
      ]);

      const dashboard = dashboardRes?.data?.metrics || null;
      const assessment = assessmentRes?.data?.assessments?.[0] || null;
      const latestVitals = latestVitalsRes?.data?.vitals || null;
      const summary = summaryRes?.data?.summary || null;

      console.log("Fetched data:", { dashboard, assessment, latestVitals, summary });
      setDashboardData(dashboard);
      setAssessmentRecord(assessment);
      setLatestVitalsRecord(latestVitals);
      setSummaryData(summary);
    } catch (err) {
      console.error("Failed to fetch metric detail data:", err);
      setError("Unable to load metric details right now.");
    } finally {
      setLoading(false);
    }
  }, [metricConfig]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const currentValue = useMemo(() => {
    if (!metricConfig) return "";

    const dashboardLatest = dashboardData?.vitals?.latest || {};
    const latestVitals = latestVitalsRecord || {};

    switch (metricConfig.field) {
      case "heartRate": {
        // Handle both array format (backend) and direct value
        if (Array.isArray(latestVitals.heartRate) && latestVitals.heartRate.length > 0) {
          const latest = latestVitals.heartRate[latestVitals.heartRate.length - 1];
          return latest?.value ?? "";
        }
        return latestVitals.heartRate ?? dashboardLatest.heartRate ?? "";
      }
      case "bloodPressure": {
        // Handle array format from backend
        if (Array.isArray(latestVitals.bloodPressure) && latestVitals.bloodPressure.length > 0) {
          const latest = latestVitals.bloodPressure[latestVitals.bloodPressure.length - 1];
          return latest?.raw || `${latest?.systolic}/${latest?.diastolic}` || "";
        }
        // Handle object format
        if (latestVitals?.bloodPressure?.raw) {
          return latestVitals.bloodPressure.raw;
        }
        // Handle direct string
        return latestVitals.bloodPressure || dashboardLatest?.bloodPressure || "";
      }
      case "oxygenLevel": {
        if (Array.isArray(latestVitals.oxygenLevel) && latestVitals.oxygenLevel.length > 0) {
          const latest = latestVitals.oxygenLevel[latestVitals.oxygenLevel.length - 1];
          return latest?.value ?? "";
        }
        return latestVitals.oxygenLevel ?? dashboardLatest.oxygenLevel ?? "";
      }
      case "bloodSugar": {
        if (Array.isArray(latestVitals.bloodSugar) && latestVitals.bloodSugar.length > 0) {
          const latest = latestVitals.bloodSugar[latestVitals.bloodSugar.length - 1];
          return latest?.value ?? "";
        }
        return latestVitals?.bloodSugar?.value ?? dashboardLatest?.bloodSugar?.value ?? "";
      }
      case "temperature": {
        if (Array.isArray(latestVitals.temperature) && latestVitals.temperature.length > 0) {
          const latest = latestVitals.temperature[latestVitals.temperature.length - 1];
          return latest?.value ?? "";
        }
        return latestVitals.temperature ?? dashboardLatest.temperature ?? "";
      }
      case "weight": {
        if (Array.isArray(latestVitals.weight) && latestVitals.weight.length > 0) {
          const latest = latestVitals.weight[latestVitals.weight.length - 1];
          return latest?.value ?? "";
        }
        return latestVitals.weight ?? dashboardLatest.weight ?? "";
      }
      case "hydration": {
        if (Array.isArray(latestVitals.hydration) && latestVitals.hydration.length > 0) {
          const latest = latestVitals.hydration[latestVitals.hydration.length - 1];
          return latest?.value ?? "";
        }
        return latestVitals.hydration ?? "";
      }
      case "fitnessLevel":
        return assessmentRecord?.fitnessLevel ?? "";
      case "sleepLevel":
        return assessmentRecord?.sleepLevel ?? "";
      case "mood":
        return assessmentRecord?.mood ?? "";
      case "appointments":
        return summaryData?.appointments?.upcoming ?? "";
      case "symptoms": {
        const symptomsList = Array.isArray(latestVitals?.symptoms) ? latestVitals.symptoms : [];
        return symptomsList.length;
      }
      case "medication": {
        const meds = dashboardData?.medications || {};
        if (typeof meds?.todayTaken === "number" && typeof meds?.todayScheduled === "number") {
          return `${meds.todayTaken}/${meds.todayScheduled}`;
        }
        return "";
      }
      default:
        return "";
    }
  }, [assessmentRecord, dashboardData, latestVitalsRecord, metricConfig, summaryData]);

  useEffect(() => {
    setValue(currentValue === null || currentValue === undefined ? "" : String(currentValue));
  }, [currentValue]);

  const validation = useMemo(() => getMetricValidation(metricConfig, value), [metricConfig, value]);

  const submitUpdate = async () => {
    if (!metricConfig || metricConfig.kind === "readonly") return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (metricConfig.kind === "vitals") {
        const payload = {};

        if (metricConfig.field === "bloodPressure") {
          const bp = parseBloodPressure(value);
          if (!bp) {
            throw new Error("Please enter blood pressure in format systolic/diastolic (example: 120/80).");
          }
          // Send as string - backend will parse it
          payload.bloodPressure = bp.raw;
        } else {
          const numericValue = Number(value);
          if (!Number.isFinite(numericValue)) {
            throw new Error("Please enter a valid numeric value.");
          }
          payload[metricConfig.field] = numericValue;
        }

        // Add source and device info for tracking
        payload.source = "manual";
        payload.deviceName = "Web App";

        console.log("Sending vitals payload:", payload);
        await api.post("/api/vitals", payload);
        emitHealthDataUpdated("vitals:metric-detail");
      }

      if (metricConfig.kind === "assessment") {
        const existing = assessmentRecord || {};
        const next = { ...existing };

        if (metricConfig.inputType === "number") {
          const numericValue = Number(value);
          if (!Number.isFinite(numericValue)) {
            throw new Error("Please enter a valid numeric value.");
          }
          next[metricConfig.field] = numericValue;
        } else {
          next[metricConfig.field] = String(value || "").trim();
        }

        console.log("Sending assessment payload:", next);
        await api.put("/api/assessment", next);
        emitHealthDataUpdated("assessment:metric-detail");
      }

      setSuccess(`${metricConfig.label} updated successfully.`);
      await fetchAllData();
    } catch (err) {
      console.error("Update error:", err);
      // Handle API error response
      const errorMessage = err?.response?.data?.message || err?.message || "Unable to update this metric.";
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!metricConfig) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <i
            onClick={() => navigate(-1)}
            className="fi fi-br-angle-left"
            style={{ color: "var(--primary)", fontSize: 20, cursor: "pointer" }}
          />
          <h3 style={{ margin: 0 }}>Metric not found</h3>
        </div>
        <p style={{ color: "#64748b" }}>The selected metric is not configured yet.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, paddingBottom: 30, maxWidth: 760, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <i
          onClick={() => navigate(-1)}
          className="fi fi-br-angle-left"
          style={{ color: "var(--primary)", fontSize: 20, cursor: "pointer" }}
        />
        <h3 style={{ margin: 0, fontSize: 22, color: "#111827" }}>{metricConfig.label}</h3>
      </div>

      <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 13 }}>
        {metricConfig.category} metric
      </p>

      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Info size={16} color="#475569" />
          <strong style={{ color: "#1f2937", fontSize: 14 }}>What this metric means</strong>
        </div>
        <p style={{ margin: 0, color: "#475569", fontSize: 13, lineHeight: 1.6 }}>{metricConfig.note}</p>
      </div>

      {loading ? (
        <div style={{ color: "#64748b" }}>Loading metric details...</div>
      ) : (
        <>
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 16,
              background: "#fff",
              padding: 16,
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>Current value</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>
              {currentValue !== "" && currentValue !== null && currentValue !== undefined
                ? `${currentValue}${metricConfig.unit ? ` ${metricConfig.unit}` : ""}`
                : "No data yet"}
            </div>
          </div>

          {metricConfig.kind === "readonly" ? (
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                background: "#fff",
                padding: 16,
              }}
            >
              <div style={{ marginBottom: 12, color: "#334155", fontSize: 14 }}>
                This metric is managed through your appointment workflow.
              </div>
              <button
                type="button"
                onClick={() => navigate("/appoint")}
                style={{
                  border: "none",
                  borderRadius: 10,
                  background: "var(--primary)",
                  color: "#fff",
                  padding: "10px 14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Open Appointment Timeline
              </button>
            </div>
          ) : (
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                background: "#fff",
                padding: 16,
              }}
            >
              <div style={{ marginBottom: 10, color: "#334155", fontSize: 14, fontWeight: 600 }}>Update value</div>

              {metricConfig.inputType === "select" ? (
                <select
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  style={{
                    width: "100%",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    marginBottom: 10,
                  }}
                >
                  <option value="">Select an option</option>
                  {(metricConfig.options || []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={metricConfig.inputType}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  min={metricConfig.min}
                  max={metricConfig.max}
                  step={metricConfig.step || "any"}
                  placeholder={metricConfig.placeholder || "Enter value"}
                  style={{
                    width: "100%",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    marginBottom: 10,
                  }}
                />
              )}

              {metricConfig.field === "bloodPressure" ? (
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
                  Quick guide: Normal around 120/80, elevated often starts near 130/80.
                </div>
              ) : null}

              {validation.message ? (
                <div
                  style={{
                    fontSize: 12,
                    marginBottom: 10,
                    color:
                      validation.level === "danger"
                        ? "#dc2626"
                        : validation.level === "warning"
                        ? "#d97706"
                        : validation.level === "success"
                        ? "#16a34a"
                        : "#64748b",
                    background:
                      validation.level === "danger"
                        ? "#fef2f2"
                        : validation.level === "warning"
                        ? "#fffbeb"
                        : validation.level === "success"
                        ? "#f0fdf4"
                        : "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                >
                  {validation.message}
                </div>
              ) : null}

              {error ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#dc2626", fontSize: 13, marginBottom: 10 }}>
                  <AlertTriangle size={14} />
                  {error}
                </div>
              ) : null}

              {success ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#16a34a", fontSize: 13, marginBottom: 10 }}>
                  <CheckCircle2 size={14} />
                  {success}
                </div>
              ) : null}

              <button
                type="button"
                onClick={submitUpdate}
                disabled={saving}
                style={{
                  border: "none",
                  borderRadius: 10,
                  background: saving ? "#94a3b8" : "var(--primary)",
                  color: "#fff",
                  padding: "10px 14px",
                  fontWeight: 600,
                  cursor: saving ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Save size={14} />
                {saving ? "Saving..." : "Save Update"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}